import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { Search } from "lucide-react";
import { offeredSubjectKinds, useSessionPreferences } from "../../../../center";

import {
    DateRangeFilter,
    FILTER_LABEL,
    formatCount,
    formatMoney,
    toAmount,
    usePagedRows,
} from "../../../../../shared/components/reports";
import { Pagination } from "../../../../../shared/ui/common/Pagination";
import Label from "../../../../../shared/ui/form/Label";
import Input from "../../../../../shared/ui/form/input/InputField";
import {
    hasModuleCoverage,
    ModuleCoverageCell,
    ModuleCoverageHeading,
    SubjectTypeFilter,
    subjectName,
    usePractitionerReport,
    type ReportSubjectRow,
    type SubjectTypeOption,
} from "../../../../reports";

interface Props {
    centerId: string;
    from: string;
    to: string;
    onFromChange: (day: string) => void;
    onToChange: (day: string) => void;
    onClear: () => void;
}

const HEADING = "py-2 pr-4 font-medium";
const CELL = "py-2.5 pr-4 text-content-primary";
const NUMBER = `${CELL} text-right tabular-nums`;

/**
 * Every patient or student the centre saw in the window, across its
 * professionals — found by name or kind — and the way into each one's report.
 */
export default function CenterStudentReports({ centerId, from, to, onFromChange, onToChange, onClear }: Props) {
    const { t, i18n } = useTranslation();
    const locale = i18n.language;
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState<SubjectTypeOption>("all");
    const { modes } = useSessionPreferences();
    const scope = useMemo(() => ({ kind: "centre" as const, centerId }), [centerId]);
    const filters = useMemo(() => ({ from, to, granularity: "month" as const, subject: "", course: "" }), [from, to]);
    const { report, loading, error, retry } = usePractitionerReport(scope, filters);

    const groupOf = (count: number) => t("reports.groupOf", { count });
    const all = report?.subjects ?? [];
    const counts: Record<SubjectTypeOption, number> = {
        all: all.length,
        patient: all.filter((row) => row.type === "patient").length,
        couple: all.filter((row) => row.type === "couple").length,
        group: all.filter((row) => row.type === "group").length,
    };
    const rows = useMemo(() => {
        const needle = search.trim().toLocaleLowerCase(locale);
        return all
            .filter((row) => typeFilter === "all" || row.type === typeFilter)
            .filter((row) => !needle || subjectName(row, groupOf).toLocaleLowerCase(locale).includes(needle))
            .sort((a, b) => subjectName(a, groupOf).localeCompare(subjectName(b, groupOf), locale));
        // groupOf is stable for a given language.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [all, typeFilter, search, locale]);
    const paged = usePagedRows(rows);
    const withCourse = report?.training ?? false;
    const withCoverage = hasModuleCoverage(all);
    const count = (value: number) => formatCount(value, locale);

    const link = (row: ReportSubjectRow) => ({
        pathname: `/center/reports/students/${row.type}/${row.id}`,
        state: { label: subjectName(row, groupOf) },
    });

    return (
        <>
            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <DateRangeFilter
                    idPrefix="student-reports"
                    from={from}
                    to={to}
                    onFromChange={onFromChange}
                    onToChange={onToChange}
                    onClear={() => {
                        setSearch("");
                        setTypeFilter("all");
                        onClear();
                    }}
                >
                    <div className="space-y-1.5">
                        <Label htmlFor="student-reports-search" className={FILTER_LABEL}>
                            {t("reports.students.search")}
                        </Label>
                        <Input
                            id="student-reports-search"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            placeholder={t("reports.searchPatient")}
                            startIcon={<Search className="h-4 w-4" aria-hidden="true" />}
                        />
                    </div>
                </DateRangeFilter>
            </section>

            <section className="rounded-2xl border border-gray-200 bg-white p-6 dark:border-gray-800 dark:bg-white/[0.03]">
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <h2 className="text-base font-semibold text-content-primary">{t("reports.students.title")}</h2>
                    {all.length > 0 && <SubjectTypeFilter value={typeFilter} onChange={setTypeFilter} counts={counts} kinds={offeredSubjectKinds(modes)} />}
                </div>

                {loading && (
                    <p role="status" className="text-sm text-content-secondary">
                        {t("common.loading")}
                    </p>
                )}
                {error && (
                    <div role="alert" className="flex items-center justify-between gap-3 text-sm text-status-critical">
                        <span>{t("reports.error")}</span>
                        <button type="button" onClick={retry} className="font-medium underline">
                            {t("reports.retry")}
                        </button>
                    </div>
                )}
                {report && rows.length === 0 && (
                    <p className="rounded-xl bg-surface-sunken p-6 text-center text-sm text-content-tertiary">
                        {all.length === 0 ? t("reports.empty") : t("reports.table.emptyType")}
                    </p>
                )}
                {report && rows.length > 0 && (
                    <div className="overflow-x-auto">
                        <table className="w-full min-w-[640px] text-left text-sm">
                            <thead>
                                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-content-tertiary dark:border-gray-800">
                                    <th scope="col" className={HEADING}>{t("reports.table.subject")}</th>
                                    {withCourse && <th scope="col" className={HEADING}>{t("reports.table.course")}</th>}
                                    {withCoverage && <ModuleCoverageHeading className={`${HEADING} text-right`} />}
                                    <th scope="col" className={HEADING}>{t("reports.table.type")}</th>
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.table.sessions")}</th>
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.status.finished")}</th>
                                    <th scope="col" className={`${HEADING} text-right`}>{t("reports.status.cancelled")}</th>
                                    {report.has_amounts && (
                                        <th scope="col" className={`${HEADING} text-right`}>{t("reports.table.amount")}</th>
                                    )}
                                    <th scope="col" className={HEADING}>
                                        <span className="sr-only">{t("reports.center.viewReport")}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {paged.page.map((row) => (
                                    <tr key={`${row.type}:${row.id}`} className="border-b border-gray-100 last:border-0 dark:border-gray-800">
                                        <th scope="row" className={`${CELL} font-medium`}>
                                            <Link to={link(row)} className="font-medium text-brand-600 underline-offset-2 hover:underline dark:text-brand-300">
                                                {subjectName(row, groupOf)}
                                            </Link>
                                        </th>
                                        {withCourse && <td className={CELL}>{row.course ?? "—"}</td>}
                                        {withCoverage && <ModuleCoverageCell row={row} className={NUMBER} />}
                                        <td className={CELL}>{t(`reports.type.${row.session_type}`)}</td>
                                        <td className={NUMBER}>{count(row.sessions)}</td>
                                        <td className={NUMBER}>{count(row.finished)}</td>
                                        <td className={NUMBER}>{count(row.cancelled)}</td>
                                        {report.has_amounts && (
                                            <td className={NUMBER}>
                                                {row.amount === null
                                                    ? t("reports.table.noRate")
                                                    : formatMoney(toAmount(row.amount), locale)}
                                            </td>
                                        )}
                                        <td className="py-1 text-right">
                                            <Link
                                                to={link(row)}
                                                className="inline-flex min-h-[44px] items-center rounded-lg px-3 text-sm font-medium text-brand-600 hover:bg-brand-50 dark:text-brand-300 dark:hover:bg-white/5"
                                            >
                                                {t("reports.center.viewReport")}
                                                <span className="sr-only">: {subjectName(row, groupOf)}</span>
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                <Pagination
                    offset={paged.offset}
                    total={paged.total}
                    pageSize={paged.pageSize}
                    onPrev={paged.onPrev}
                    onNext={paged.onNext}
                />
            </section>
        </>
    );
}
